import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Power, RefreshCw } from "lucide-react";
import { PaginationBar, usePagination } from "@/components/Pagination";

const MASTER_TYPES = [
  { key: "items", label: "Items" },
  { key: "companies", label: "Companies" },
  { key: "categories", label: "Categories" },
  { key: "groups", label: "Groups" },
  { key: "sectors", label: "Sectors" },
  { key: "hsn_codes", label: "HSN Codes" },
];

const ROLE_LABELS = { super_admin: "Super Admin", support_admin: "Support Admin", billing_admin: "Billing Admin" };

function StatusBadge({ status }) {
  const map = {
    pending: { text: "Pending", variant: "secondary" },
    active: { text: "Active", variant: "default" },
    inactive: { text: "Inactive", variant: "outline" },
    rejected: { text: "Rejected", variant: "destructive" },
    approved: { text: "Approved", variant: "default" },
  };
  const m = map[status] || { text: status || "-", variant: "outline" };
  return <Badge data-testid={`status-badge-${status}`} variant={m.variant}>{m.text}</Badge>;
}

function CountBadge({ n, testId }) {
  return <span data-testid={testId} className="ml-2 inline-flex items-center justify-center min-w-6 h-6 px-1.5 rounded-full bg-primary/15 text-primary text-xs font-bold">{n}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const platformRole = user?.platform_role || "super_admin";
  const isSuper = platformRole === "super_admin";
  const isSupport = platformRole === "support_admin";
  const isBilling = platformRole === "billing_admin";
  const showAdminUsers = location.pathname.endsWith("/admin-users") || params.get("tab") === "admins";

  const [tenants, setTenants] = useState([]);
  const [requests, setRequests] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const pg = usePagination(adminUsers);
  const [rejectOrg, setRejectOrg] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [noAccess, setNoAccess] = useState(false); // role without dashboard access (403)

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const jobs = [api.get("/admin/tenants").then(r => setTenants(r.data))];
      if (isSuper || isSupport) jobs.push(api.get("/admin/master-requests").then(r => setRequests(r.data)));
      if (isSuper) jobs.push(api.get("/admin/admin-users").then(r => setAdminUsers(r.data)));
      await Promise.all(jobs);
      setLastRefresh(new Date());
    } catch (e) {
      if (e.response?.status === 403) setNoAccess(true);
      else if (!silent) toast.error("Failed to load admin data");
    }
    finally { setRefreshing(false); }
  };
  useEffect(() => { load(); }, []);
  // Real-time-ish: poll every 30s so the summary reflects the latest status
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, []);

  const pending = useMemo(() => tenants.filter((t) => t.status === "pending"), [tenants]);
  const pg2 = usePagination(pending);
  const inactive = useMemo(() => tenants.filter((t) => t.status === "inactive"), [tenants]);
  const pg4 = usePagination(inactive);
  const rejected = useMemo(() => tenants.filter((t) => t.status === "rejected"), [tenants]);
  const pg3 = usePagination(rejected);
  const pendingReq = useMemo(() => requests.filter((r) => r.status === "pending"), [requests]);
  const pg5 = usePagination(pendingReq);

  const approve = async (oid, starter_catalog) => {
    try {
      await api.post(`/admin/tenants/${oid}/approve`, { starter_catalog });
      toast.success(starter_catalog ? "Approved with starter catalog activated" : "Approved");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const submitReject = async () => {
    if (!rejectOrg) return;
    try {
      await api.post(`/admin/tenants/${rejectOrg.id}/reject`, { reason: rejectReason || null });
      toast.success("Rejected");
      setRejectOrg(null); setRejectReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approveReq = async (rid) => {
    try { await api.post(`/admin/master-requests/${rid}/approve`); toast.success("Request approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rejectReq = async (rid) => {
    try { await api.post(`/admin/master-requests/${rid}/reject`); toast.success("Request rejected"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reactivate = async (oid) => {
    try { await api.post(`/admin/tenants/${oid}/reactivate`); toast.success("Organisation reactivated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleAdmin = async (uid) => {
    try { await api.post(`/admin/admin-users/${uid}/toggle-active`); toast.success("Status updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteAdmin = async (uid, name) => {
    if (!confirm(`Delete admin user "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/admin-users/${uid}`); toast.success("Admin user deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // ---- SaaS Admin User view (own page at /admin/admin-users) ----
  if (showAdminUsers && isSuper) {
    return (
      <div className="p-6 space-y-6" data-testid="admin-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">SaaS Admin User</h1>
            <p className="text-muted-foreground">Platform-level administrators with scoped permissions.</p>
          </div>
          <Button size="sm" onClick={() => nav("/admin/users/new")} data-testid="new-admin-user-btn">
            <Plus className="w-4 h-4 mr-1"/> New Admin User
          </Button>
        </div>
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg.pageItems.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell><Badge variant="outline">{ROLE_LABELS[u.platform_role] || u.platform_role}</Badge></TableCell>
                    <TableCell><StatusBadge status={u.is_active !== false ? "active" : "rejected"} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDateTime(u.last_login_at)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => nav(`/admin/users/${u.id}/edit`)} data-testid={`admin-user-edit-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5"/>
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleAdmin(u.id)}
                        data-testid={`admin-user-toggle-${u.id}`} title={u.is_active !== false ? "Deactivate" : "Activate"}>
                        <Power className={`w-3.5 h-3.5 ${u.is_active !== false ? "text-destructive" : "text-green-500"}`}/>
                      </Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteAdmin(u.id, u.name)} data-testid={`admin-user-delete-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!adminUsers.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No admin users</TableCell></TableRow>}
              </TableBody>
            </Table>
            <PaginationBar pg={pg}/>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Platform summary dashboard (/admin) ----
  return (
    <div className="p-6 space-y-6" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Platform</h1>
          <p className="text-muted-foreground">
            {isSuper && "Organisation approvals, master requests, and tenant summary."}
            {isSupport && "View organisations and manage support requests."}
            {isBilling && "Manage subscription plans and payments."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastRefresh && <span className="text-xs text-muted-foreground" data-testid="last-refresh">Updated {lastRefresh.toLocaleTimeString("en-IN")}</span>}
          <Button size="sm" variant="outline" onClick={() => load()} disabled={refreshing} data-testid="refresh-dashboard">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`}/> Refresh
          </Button>
        </div>
      </div>

      {noAccess && (
        <Card data-testid="dashboard-no-access">
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Your role does not include the SaaS Dashboard. Use the navigation above to open the pages you are authorised to access.
          </CardContent>
        </Card>
      )}

      {/* 1. Pending Organisations */}
      {(isSuper || isSupport || isBilling) && (
        <Card data-testid="section-pending">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">Pending Organisations <CountBadge n={pending.length} testId="count-pending" /></CardTitle>
            <CardDescription>Registered, awaiting approval or activation.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation Name</TableHead>
                  <TableHead>Registration Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg2.pageItems.map((t) => (
                  <TableRow key={t.id} data-testid={`pending-row-${t.id}`}>
                    <TableCell className="font-medium">{t.business_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {isSuper ? <>
                        <Button size="sm" onClick={() => approve(t.id, true)} data-testid={`admin-approve-starter-${t.id}`}>Approve + Starter</Button>
                        <Button size="sm" variant="outline" onClick={() => approve(t.id, false)} data-testid={`admin-approve-${t.id}`}>Approve</Button>
                        <Button size="sm" variant="destructive" onClick={() => { setRejectOrg(t); setRejectReason(""); }} data-testid={`admin-reject-${t.id}`}>Reject</Button>
                      </> : <span className="text-xs text-muted-foreground">View only</span>}
                    </TableCell>
                  </TableRow>
                ))}
                {!pending.length && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No pending organisations</TableCell></TableRow>}
              </TableBody>
            </Table>
            <PaginationBar pg={pg2} testid="pagination-2"/>
          </CardContent>
        </Card>
      )}

      {/* 2. Rejected Organisations */}
      {(isSuper || isSupport || isBilling) && (
        <Card data-testid="section-rejected">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">Rejected Organisations <CountBadge n={rejected.length} testId="count-rejected" /></CardTitle>
            <CardDescription>Registrations that were turned down.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation Name</TableHead>
                  <TableHead>Rejection Date</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg3.pageItems.map((t) => (
                  <TableRow key={t.id} data-testid={`rejected-row-${t.id}`}>
                    <TableCell className="font-medium">{t.business_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(t.rejected_at)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{t.rejection_reason || "—"}</TableCell>
                  </TableRow>
                ))}
                {!rejected.length && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">No rejected organisations</TableCell></TableRow>}
              </TableBody>
            </Table>
            <PaginationBar pg={pg3} testid="pagination-3"/>
          </CardContent>
        </Card>
      )}

      {/* 3. Inactive (deactivated) Organisations — restored from here */}
      {(isSuper || isSupport || isBilling) && inactive.length > 0 && (
        <Card data-testid="section-inactive">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">Inactive Organisations <CountBadge n={inactive.length} testId="count-inactive" /></CardTitle>
            <CardDescription>Deactivated tenants — their users cannot sign in until reactivated.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation Name</TableHead>
                  <TableHead>Deactivated On</TableHead>
                  {isSuper && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg4.pageItems.map((t) => (
                  <TableRow key={t.id} data-testid={`inactive-row-${t.id}`}>
                    <TableCell className="font-medium">{t.business_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(t.deactivated_at)}</TableCell>
                    {isSuper && (
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => reactivate(t.id)} data-testid={`admin-reactivate-${t.id}`}>Reactivate</Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationBar pg={pg4} testid="pagination-4"/>
          </CardContent>
        </Card>
      )}

      {/* 4. Master Data Requests */}
      {(isSuper || isSupport) && (
        <Card data-testid="section-requests">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center">Master Data Requests <CountBadge n={pendingReq.length} testId="count-requests" /></CardTitle>
            <CardDescription>Pending requests from organisations to activate shared master data.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation Name</TableHead>
                  <TableHead>Requested Master Data</TableHead>
                  <TableHead>Request Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pg5.pageItems.map((r) => (
                  <TableRow key={r.id} data-testid={`admin-req-row-${r.id}`}>
                    <TableCell className="font-medium">{r.org_name}</TableCell>
                    <TableCell>{MASTER_TYPES.find(m => m.key === r.master)?.label || r.master}
                      {(r.ids || []).length > 0 && <span className="text-xs text-muted-foreground"> · {r.ids.length} record{r.ids.length > 1 ? "s" : ""}</span>}
                      {r.note && <div className="text-xs text-muted-foreground mt-0.5">{r.note}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" onClick={() => approveReq(r.id)} data-testid={`admin-req-approve-${r.id}`}>Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => rejectReq(r.id)} data-testid={`admin-req-reject-${r.id}`}>Reject</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!pendingReq.length && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No pending requests</TableCell></TableRow>}
              </TableBody>
            </Table>
            <PaginationBar pg={pg5} testid="pagination-5"/>
          </CardContent>
        </Card>
      )}

      {/* Reject-with-reason dialog */}
      <Dialog open={!!rejectOrg} onOpenChange={(v) => { if (!v) setRejectOrg(null); }}>
        <DialogContent className="max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject · {rejectOrg?.business_name}</DialogTitle>
            <CardDescription>Optionally record a reason — it will be shown on the Rejected Organisations list.</CardDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Incomplete KYC documents" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOrg(null)} data-testid="reject-cancel">Cancel</Button>
            <Button variant="destructive" onClick={submitReject} data-testid="reject-confirm">Reject Organisation</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
